import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { getUrlParams, formatURL } from '../../../../utils';
import { addPreventScrollStyle, removePreventScrollStyle } from '../../../../utils/preventScroll';
import {
  openModalOperatorConfirmation,
  closeModalOperatorConfirmation,
  setOperatorConfirmationClicked,
  cleanStatesPurchaseMobile,
  cleanErrors,
} from '../../../../actions/purchaseActions';
import { formatDateBrFromDate, toISO } from '../../../../views/v2/search/utils/searchParsing';
import { fetchProductQuotation } from '../../../../apiCalls';
import ModalOperatorConfirmationV2View from './ModalOperatorConfirmationV2View';
import ModalPlanIncompatible from './ModalPlanIncompatible';

const daysDifference = (startDate, endDate) => {
  const diffTime = Math.abs(new Date(endDate) - new Date(startDate));
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const INCOMPATIBLE_PLAN_TAG_ID = 91;

export const ModalOperatorConfirmationV2 = ({
  isModalOperatorConfirmationActive,
  openModalOperatorConfirmation,
  closeModalOperatorConfirmation,
  setOperatorConfirmationClicked,
  cleanStatesPurchaseMobile,
  cleanErrors,
  plans,
  providers,
  destinations,
  history,
  match,
}) => {
  const hasCheckedParams = useRef(false);
  const originalTrip = useRef({
    destination: match.params.destination || '',
    beginDate: match.params.begin_date || '',
    endDate: match.params.end_date || '',
  });
  const [isChecked, setIsChecked] = useState(false);
  const [destinationSlug, setDestinationSlug] = useState('');
  const [destinationDropdownOpen, setDestinationDropdownOpen] = useState(false);
  const [range, setRange] = useState({ startDate: null, endDate: null });
  const [isMobileDatepickerOpen, setIsMobileDatepickerOpen] = useState(false);
  const [datepickMode, setDatepickMode] = useState('range');
  const [isDesktopPickerOpen, setIsDesktopPickerOpen] = useState(false);
  const [desktopPickerMode, setDesktopPickerMode] = useState('range');
  const [isConfirming, setIsConfirming] = useState(false);
  const [showIncompatible, setShowIncompatible] = useState(false);

  useEffect(() => {
    if (hasCheckedParams.current) return;
    const params = getUrlParams();
    if (params.operator !== undefined) openModalOperatorConfirmation();
    hasCheckedParams.current = true;
  }, [openModalOperatorConfirmation]);

  useEffect(() => {
    if (isModalOperatorConfirmationActive) {
      addPreventScrollStyle();
    } else {
      removePreventScrollStyle();
    }
    return () => removePreventScrollStyle();
  }, [isModalOperatorConfirmationActive]);

  const openDatepickerFor = useCallback(
    (mode) => {
      const { startDate, endDate } = range;

      let effective = mode;
      if (!startDate && !endDate) {
        effective = 'range';
      } else if (startDate && endDate) {
        effective = 'range';
      } else if (mode !== 'start' && mode !== 'end') {
        effective = 'range';
      }

      if (typeof window !== 'undefined' && window.innerWidth >= 769) {
        setDesktopPickerMode(effective);
        setIsDesktopPickerOpen(true);
      } else {
        setDatepickMode(effective);
        setIsMobileDatepickerOpen(true);
      }
    },
    [range]
  );

  const findDestinationBySlug = (slug) => {
    if (!slug || !destinations) {
      return null;
    }

    return destinations.find((d) => d.slug === slug);
  };

  const { providerName, providerImgPath } = useMemo(() => {
    if (!providers || !plans || !plans.length) return { providerName: '', providerImgPath: '' };
    const currentPlan = plans[0];
    const provider = providers.find((p) => String(p.id) === String(currentPlan.provider_code));
    if (!provider) return { providerName: '', providerImgPath: '' };
    return {
      providerName: provider.name,
      providerImgPath: `/assets/images/seguradoras/${provider.slug}.png`,
    };
  }, [providers, plans]);

  const selectedDestination = useMemo(() => {
    if (!destinationSlug || !destinations) {
      return '';
    }

    return destinations.find((d) => d.slug === destinationSlug);
  }, [destinationSlug, destinations]);

  const hasClientAltered = useMemo(() => {
    const { destination, beginDate, endDate } = originalTrip.current;
    const EQUIVALENT_DESTINATIONS = [new Set(['america-do-sul', 'argentina'])];

    if (!destination && !beginDate && !endDate) {
      return false;
    }

    const originalDestination = findDestinationBySlug(destination);
    const isDestinationAmericaDoSulEquivalent = (a, b) => EQUIVALENT_DESTINATIONS.some((pair) => pair.has(a) && pair.has(b));

    let originalDestinationSlug = originalDestination ? originalDestination.slug : null;
    if (originalDestination && originalDestination.promotion_destination_slug) {
      originalDestinationSlug = originalDestination.promotion_destination_slug;
    }

    let selectedDestinationSlug = destinationSlug;
    if (selectedDestination && selectedDestination.promotion_destination_slug) {
      selectedDestinationSlug = selectedDestination.promotion_destination_slug;
    }

    if (toISO(range.startDate) !== beginDate || toISO(range.endDate) !== endDate) {
      return true;
    }

    // Consider america-do-sul equivalent destinations to avoid false positives.
    if (isDestinationAmericaDoSulEquivalent(selectedDestinationSlug, originalDestinationSlug)) {
      return false
    }

    if (selectedDestinationSlug !== originalDestinationSlug) {
      return true;
    }

    return false;
  }, [destinationSlug, range]);

  const isFormComplete = !!(destinationSlug && range.startDate && range.endDate);
  const plan = plans && plans[0];
  const elderPrice = plan && plan.elder && plan.elder.price;
  const hasElderRange = plan && plan.elder && plan.elder.min_age < plan.elder.max_age;
  const departureBr = formatDateBrFromDate(range.startDate);
  const returnBr = formatDateBrFromDate(range.endDate);
  const tripDays = range.startDate && range.endDate ? daysDifference(range.startDate, range.endDate) : null;

  const handleConfirm = useCallback(() => {
    if (!destinationSlug || !range.startDate || !range.endDate || !plans || !plans.length) {
      return;
    }

    setIsConfirming(true);
    fetchProductQuotation({
      begin_date: toISO(range.startDate),
      end_date: toISO(range.endDate),
      destination: destinationSlug,
      product_code: String(plans[0].code),
    })
      .then(function (res) {
        return res.ok ? res.json() : Promise.reject();
      })
      .then(function (data) {
        if (data && data[0] && String(data[0].code) === String(plans[0].code)) {
          closeModalOperatorConfirmation();

          const url = `/pedido/${String(plans[0].code)}/${destinationSlug}/${toISO(range.startDate)}/${toISO(range.endDate)}/`;
          history.push({
            pathname: formatURL(match.params, url, null, true),
            search: hasClientAltered ? `?tag=${INCOMPATIBLE_PLAN_TAG_ID}` : undefined,
          });
        } else {
          setShowIncompatible(true);
        }
      })
      .catch(function () {
        setShowIncompatible(true);
      })
      .then(function () {
        setIsConfirming(false);
      });
  }, [destinationSlug, range, plans, closeModalOperatorConfirmation, hasClientAltered]);

  const handleChooseOtherPlan = useCallback(() => {
    cleanStatesPurchaseMobile();
    cleanErrors();
    history.push({
      pathname: formatURL(
        match.params,
        '/pesquisa/' + destinationSlug + '/' + toISO(range.startDate) + '/' + toISO(range.endDate) + '/',
        null,
        true
      ),
      search: `?tag=${INCOMPATIBLE_PLAN_TAG_ID}`,
    });
  }, [cleanStatesPurchaseMobile, cleanErrors, history, match, destinationSlug, range]);

  const handleContactSupport = useCallback(() => {
    window.open('https://api.whatsapp.com/send?phone=553139727083&text=Seguros%20Promo', '_blank');
  }, []);

  if (!isModalOperatorConfirmationActive) return null;

  return (
    <React.Fragment>
      {showIncompatible ? (
        <ModalPlanIncompatible
          onClose={function () {
            setShowIncompatible(false);
          }}
          onChooseOtherPlan={handleChooseOtherPlan}
          onContactSupport={handleContactSupport}
        />
      ) : (
        <ModalOperatorConfirmationV2View
          destinations={destinations}
          selectedDestinationName={selectedDestination ? selectedDestination.name : ''}
          destinationDropdownOpen={destinationDropdownOpen}
          onToggleDestinationDropdown={function () {
            setDestinationDropdownOpen(function (prev) {
              return !prev;
            });
          }}
          onSelectDestination={function (slug) {
            if (slug) setDestinationSlug(slug);
            setDestinationDropdownOpen(false);
          }}
          departureBr={departureBr}
          returnBr={returnBr}
          tripDays={tripDays}
          onOpenDatepicker={openDatepickerFor}
          isMobileDatepickerOpen={isMobileDatepickerOpen}
          datepickMode={datepickMode}
          range={range}
          onRangeChange={setRange}
          onDatepickerConfirm={function (val) {
            setRange(val);
            setIsMobileDatepickerOpen(false);
          }}
          onDatepickerBack={function () {
            setIsMobileDatepickerOpen(false);
          }}
          onDatepickerClose={function () {
            setIsMobileDatepickerOpen(false);
          }}
          isDesktopPickerOpen={isDesktopPickerOpen}
          desktopPickerMode={desktopPickerMode}
          onDesktopPickerChange={function (val) {
            setRange(val);
          }}
          onDesktopPickerComplete={function (val) {
            setRange(val);
            setIsDesktopPickerOpen(false);
          }}
          onDesktopPickerClose={function () {
            setIsDesktopPickerOpen(false);
          }}
          plan={plan}
          providerImgPath={providerImgPath}
          providerName={providerName}
          elderPrice={elderPrice}
          hasElderRange={hasElderRange}
          isChecked={isChecked}
          isFormComplete={isFormComplete}
          isConfirming={isConfirming}
          onCheckChange={function (e) {
            setIsChecked(e.target.checked);
            if (e.target.checked) setOperatorConfirmationClicked();
          }}
          onConfirm={handleConfirm}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  isModalOperatorConfirmationActive: state.purchase.isModalOperatorConfirmationActive,
  plans: state.purchase.plans,
  providers: state.global.providers,
  destinations: state.global.destinations,
});

export default withRouter(
  connect(mapStateToProps, {
    openModalOperatorConfirmation,
    closeModalOperatorConfirmation,
    setOperatorConfirmationClicked,
    cleanStatesPurchaseMobile,
    cleanErrors,
  })(ModalOperatorConfirmationV2)
);



// WEBPACK FOOTER //
// ./src/components/purchase/modal_operator_confirmation/v2/ModalOperatorConfirmationV2.jsx